import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import './EnterpriseFactoryOverview.css';
import { fetchCompanyFactoryOverview } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

// 숫자 카운트 애니메이션 훅
const useCountAnimation = (end, duration = 1000) => {
  const [count, setCount] = useState(end);
  const [prevEnd, setPrevEnd] = useState(end);

  useEffect(() => {
    // duration이 0이면 즉시 값 설정 (애니메이션 없음)
    if (duration === 0) {
      setCount(end);
      setPrevEnd(end);
      return;
    }

    if (end === prevEnd) return;

    const start = prevEnd;
    const increment = (end - start) / (duration / 16);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
        setCount(end);
        setPrevEnd(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [end, prevEnd, duration]);

  return count;
};

function EnterpriseFactoryOverview() {
  const navigate = useNavigate();

  // 상태 관리
  const [factoriesData, setFactoriesData] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  // ref로 리렌더링 방지
  const refreshIndicatorRef = useRef(null);

  // 유틸리티 함수들
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}분 전`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}시간 전`;
    return date.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  const getStatusText = (status) => {
    const statusMap = {
      'OPERATING': '가동',
      'STANDBY': '대기',
      'STOPPED': '중단'
    };
    return statusMap[status] || status;
  };

  const getSeverityText = (severity) => {
    const severityMap = {
      'ERROR': '오류',
      'WARNING': '경고',
      'INFO': '정보'
    };
    return severityMap[severity] || severity;
  };

  const getLocation = (address) => {
    // address에서 첫 단어 추출 (예: "서울특별시 강남구..." → "서울")
    if (!address) return '';
    const firstWord = address.split(' ')[0];
    // "특별시", "광역시" 등 제거
    return firstWord.replace(/(특별시|광역시|도)$/, '');
  };

  // 상태별 색상 정보
  const getStatusInfo = (status) => {
    // API 응답의 OPERATING, STANDBY, STOPPED를 처리
    const normalizedStatus = status?.toUpperCase();
    switch (normalizedStatus) {
      case 'OPERATING':
        return { text: '가동', color: 'status-running', bgColor: 'status-bg-running' };
      case 'STANDBY':
        return { text: '대기', color: 'status-idle', bgColor: 'status-bg-idle' };
      case 'STOPPED':
        return { text: '중단', color: 'status-stopped', bgColor: 'status-bg-stopped' };
      // 기존 호환성 유지
      case 'RUNNING':
        return { text: '가동', color: 'status-running', bgColor: 'status-bg-running' };
      case 'IDLE':
        return { text: '대기', color: 'status-idle', bgColor: 'status-bg-idle' };
      default:
        return { text: '오류', color: 'status-stopped', bgColor: 'status-bg-stopped' };
    }
  };

  // 알람 심각도별 색상
  const getSeverityColor = (severity) => {
    const normalizedSeverity = severity?.toUpperCase();
    switch (normalizedSeverity) {
      case 'CRITICAL': return 'severity-critical';
      case 'ERROR': return 'severity-error';
      case 'WARNING': return 'severity-warning';
      case 'INFO': return 'severity-info';
      default: return 'severity-default';
    }
  };

  // API 데이터 로딩
  const loadFactoryOverviewData = useCallback(async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        // CSS 클래스로 애니메이션 처리 (리렌더링 방지)
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => {
            refreshIndicatorRef.current?.classList.remove('refreshing');
          }, 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await fetchCompanyFactoryOverview();
      setFactoriesData(data.factories || []);
      setLastSyncTime(data.lastSyncTime);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('공장 현황 조회 실패:', err);
      if (!isAutoRefresh) {
        setError('데이터를 불러오는데 실패했습니다.');
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  }, []);

  // 첫 로드 완료 여부 추적
  useEffect(() => {
    if (factoriesData.length > 0 && !isFirstLoadComplete) {
      setIsFirstLoadComplete(true);
    }
  }, [factoriesData, isFirstLoadComplete]);

  // 초기 데이터 로드
  useEffect(() => {
    loadFactoryOverviewData(false);
  }, [loadFactoryOverviewData]);

  // useAutoRefresh 훅 사용 (30초 쿨다운 적용)
  useAutoRefresh(() => {
    loadFactoryOverviewData(true);
  });

  // 공장 카드 클릭 핸들러
  const handleFactoryClick = (factoryId) => {
    navigate(`/factory?factoryId=${factoryId}`);
  };

  return (
    <MainLayout>
      <div className="factory-overview-container">
        {/* 헤더 */}
        <div className="factory-overview-header">
          <div>
            <h1 className="factory-overview-title">공장별 대시보드</h1>
            <p className="factory-overview-subtitle">전사 공장별 설비 가동 현황 및 알람 모니터링</p>
          </div>
          <div className="factory-overview-update" ref={refreshIndicatorRef}>
            <div className="update-label-container">
              <span className="update-label">업데이트</span>
              <span className="material-symbols-outlined refresh-icon">
                sync
              </span>
            </div>
            <div className="update-time">
              {loading ? '로딩 중...' : (
                lastSyncTime
                  ? new Date(lastSyncTime).toLocaleString('ko-KR')
                  : lastUpdated.toLocaleTimeString('ko-KR')
              )}
            </div>
          </div>
        </div>

        {/* 로딩 상태 */}
        {loading && (
          <div className="loading-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            fontSize: '16px',
            color: '#9CA3AF'
          }}>
            <div>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
              <div>공장 현황을 불러오는 중...</div>
            </div>
          </div>
        )}

        {/* 에러 상태 */}
        {error && !loading && (
          <div className="error-container" style={{
            backgroundColor: '#FEE2E2',
            border: '1px solid #EF4444',
            borderRadius: '8px',
            padding: '20px',
            margin: '20px 0',
            color: '#991B1B'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>데이터 로드 실패</div>
                <div>{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* 빈 데이터 */}
        {!loading && !error && factoriesData.length === 0 && (
          <div className="empty-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: '#9CA3AF'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏭</div>
            <div style={{ fontSize: '16px' }}>등록된 공장이 없습니다.</div>
          </div>
        )}

        {/* 공장별 상세 현황 */}
        {!loading && !error && factoriesData.length > 0 && (
          <div className="factory-details-grid">
            {factoriesData.map((factory) => {
            // 안전한 데이터 접근을 위한 기본값 설정
            const equipment = factory.equipment || { running: 0, idle: 0, stopped: 0, total: 0 };
            const equipmentStatus = {
              operating: equipment.running || 0,
              standby: equipment.idle || 0,
              stopped: equipment.stopped || 0,
              total: equipment.total || 0,
              operatingRate: factory.operationRate || 0
            };
            const machines = factory.machines || [];
            const recentAlarms = factory.recentAlarms || [];

            // 오늘 총 알람 수 (factory_daily_stats 기준)
            const todayTotalAlarms = factory.alarms?.total || 0;

            // 실시간 알람 기준 severity별 카운트
            const alarmSummary = {
              total: recentAlarms.length,
              critical: recentAlarms.filter(a => a.severity?.toUpperCase() === 'CRITICAL').length,
              warning: recentAlarms.filter(a => a.severity?.toUpperCase() === 'WARNING').length,
              info: recentAlarms.filter(a => a.severity?.toUpperCase() === 'INFO').length
            };

            // 각 공장별 설비 상태 데이터 생성
            const statusData = [
              { count: equipmentStatus.operating, color: '#22c55e', percentage: equipmentStatus.total > 0 ? (equipmentStatus.operating / equipmentStatus.total) * 100 : 0 },
              { count: equipmentStatus.standby, color: '#fbbf24', percentage: equipmentStatus.total > 0 ? (equipmentStatus.standby / equipmentStatus.total) * 100 : 0 },
              { count: equipmentStatus.stopped, color: '#ef4444', percentage: equipmentStatus.total > 0 ? (equipmentStatus.stopped / equipmentStatus.total) * 100 : 0 }
            ];

            return (
              <div
                key={factory.factoryId}
                className="factory-detail-card"
                onClick={() => handleFactoryClick(factory.factoryId)}
                style={{ cursor: 'pointer' }}
              >
                <div className="factory-detail-grid">
                  {/* 좌측: 공장 정보 및 설비현황 */}
                  <div className="factory-info-section">
                    <div className="factory-info-grid">
                      {/* 공장명 */}
                      <div className="factory-name-area">
                        <div className="factory-title">{factory.factoryName}</div>
                        <div className="factory-subtitle">{getLocation(factory.address)}</div>

                        {/* 3D 공장 내부 도면 이미지 */}
                        <div className="factory-3d-image">
                          <svg viewBox="0 0 200 280" xmlns="http://www.w3.org/2000/svg">
                            {/* 바닥 그리드 */}
                            <defs>
                              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#2a3441" strokeWidth="0.5"/>
                              </pattern>
                            </defs>

                            {/* 바닥 면 */}
                            <polygon points="20,250 180,250 200,230 0,230" fill="url(#grid)" stroke="#3a4451" strokeWidth="1"/>

                            {/* 좌측 벽 */}
                            <polygon points="20,40 20,250 0,230 0,30" fill="#1a1f28" stroke="#3a4451" strokeWidth="1"/>

                            {/* 우측 벽 */}
                            <polygon points="180,40 200,30 200,230 180,250" fill="#232831" stroke="#3a4451" strokeWidth="1"/>

                            {/* 천장 */}
                            <polygon points="20,40 180,40 200,30 0,30" fill="#2a2f38" stroke="#4a4f57" strokeWidth="1"/>

                            {/* 사출기 1 - 최상단 */}
                            <g>
                              <polygon points="35,80 75,80 80,75 30,75" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="35,55 75,55 80,50 30,50 30,75 35,80" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="70" y="62" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="55" cy="62" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 사출기 2 */}
                            <g>
                              <polygon points="35,130 75,130 80,125 30,125" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="35,105 75,105 80,100 30,100 30,125 35,130" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="70" y="112" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="55" cy="112" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.5s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 사출기 3 */}
                            <g>
                              <polygon points="35,180 75,180 80,175 30,175" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="35,155 75,155 80,150 30,150 30,175 35,180" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="70" y="162" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="55" cy="162" r="2.5" fill="#fbbf24" opacity="0.9"/>
                            </g>

                            {/* 사출기 4 */}
                            <g>
                              <polygon points="35,230 75,230 80,225 30,225" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="35,205 75,205 80,200 30,200 30,225 35,230" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="70" y="212" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="55" cy="212" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.2s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 사출기 5 - 우측 상단 */}
                            <g>
                              <polygon points="125,80 165,80 170,75 120,75" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="125,55 165,55 170,50 120,50 120,75 125,80" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="160" y="62" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="145" cy="62" r="2.5" fill="#ef4444" opacity="0.9"/>
                            </g>

                            {/* 사출기 6 */}
                            <g>
                              <polygon points="125,130 165,130 170,125 120,125" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="125,105 165,105 170,100 120,100 120,125 125,130" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="160" y="112" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="145" cy="112" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.8s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 사출기 7 */}
                            <g>
                              <polygon points="125,180 165,180 170,175 120,175" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="125,155 165,155 170,150 120,150 120,175 125,180" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="160" y="162" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="145" cy="162" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.6s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 사출기 8 - 우측 하단 */}
                            <g>
                              <polygon points="125,230 165,230 170,225 120,225" fill="#2d3748" stroke="#4299e1" strokeWidth="1"/>
                              <polygon points="125,205 165,205 170,200 120,200 120,225 125,230" fill="#3a4556" stroke="#4299e1" strokeWidth="1"/>
                              <rect x="160" y="212" width="22" height="7" fill="#4a5568" stroke="#60a5fa" strokeWidth="0.8"/>
                              <circle cx="145" cy="212" r="2.5" fill="#22c55e" opacity="0.9">
                                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.4s" repeatCount="indefinite"/>
                              </circle>
                            </g>

                            {/* 중앙 통로 */}
                            <rect x="95" y="50" width="10" height="190" fill="#374151" stroke="#6b7280" strokeWidth="0.5" strokeDasharray="3,3"/>

                            {/* 조명 */}
                            <ellipse cx="50" cy="42" rx="12" ry="2.5" fill="#fbbf24" opacity="0.3"/>
                            <ellipse cx="100" cy="42" rx="12" ry="2.5" fill="#fbbf24" opacity="0.3"/>
                            <ellipse cx="150" cy="42" rx="12" ry="2.5" fill="#fbbf24" opacity="0.3"/>

                            {/* 범례 */}
                            <g transform="translate(5, 5)">
                              <circle cx="5" cy="5" r="3" fill="#22c55e"/>
                              <text x="12" y="8" fontSize="7" fill="#9CA3AF">가동</text>

                              <circle cx="45" cy="5" r="3" fill="#fbbf24"/>
                              <text x="52" y="8" fontSize="7" fill="#9CA3AF">대기</text>

                              <circle cx="85" cy="5" r="3" fill="#ef4444"/>
                              <text x="92" y="8" fontSize="7" fill="#9CA3AF">정지</text>
                            </g>
                          </svg>
                        </div>
                      </div>

                      {/* 설비현황 도넛차트 */}
                      <div className="factory-donut-area">
                        <h4 className="section-title">설비현황</h4>

                        <div className="donut-chart-wrapper">
                          <svg className="donut-svg" viewBox="0 0 128 128">
                            {statusData.map((item, index) => {
                              const radius = 52;
                              const circumference = 2 * Math.PI * radius;
                              const offset = statusData.slice(0, index).reduce((sum, prev) => sum + prev.percentage, 0);
                              const strokeDasharray = `${(item.percentage / 100) * circumference} ${circumference}`;
                              const strokeDashoffset = -((offset / 100) * circumference);

                              return (
                                <circle
                                  key={index}
                                  cx="64"
                                  cy="64"
                                  r={radius}
                                  fill="transparent"
                                  stroke={item.color}
                                  strokeWidth="16"
                                  strokeDasharray={strokeDasharray}
                                  strokeDashoffset={strokeDashoffset}
                                  transform="rotate(-90 64 64)"
                                />
                              );
                            })}
                          </svg>
                          <div className="donut-center">
                            <div className="donut-percentage">{equipmentStatus.operatingRate.toFixed(1)}%</div>
                            <div className="donut-label">가동설비</div>
                          </div>
                        </div>

                        <div className="donut-legend-area">
                          {statusData.map((item, index) => (
                            <div key={index} className="donut-legend-item">
                              <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                              <span className="legend-count">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 지표들 */}
                    <div className="factory-metrics-grid">
                      <div className="metric-item">
                        <div className="metric-label">설비수</div>
                        <div className="metric-value">{equipmentStatus.total}대</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-label">가동률</div>
                        <div className="metric-value green-text">{equipmentStatus.operatingRate.toFixed(1)}%</div>
                      </div>
                      <div className="metric-item">
                        <div className="metric-label">총 알람</div>
                        <div className="metric-value red-text">{formatNumber(todayTotalAlarms)}건</div>
                      </div>
                    </div>

                    {/* 환경 데이터 */}
                    <div className="factory-environment-section">
                      <div className="environment-divider"></div>
                      <div className="environment-metrics-grid">
                        <div className="env-metric-item">
                          <span className="material-symbols-outlined env-icon temperature">thermostat</span>
                          <span className="env-value">{[23.5, 24.2, 22.8, 25.1, 23.0][factory.factoryId % 5]}°C</span>
                        </div>
                        <div className="env-metric-item">
                          <span className="material-symbols-outlined env-icon humidity">humidity_percentage</span>
                          <span className="env-value">{[55, 62, 48, 58, 52][factory.factoryId % 5]}%</span>
                        </div>
                        <div className="env-metric-item">
                          <span className="material-symbols-outlined env-icon air-quality">air</span>
                          <span className={`env-value ${[12, 18, 25, 8, 32][factory.factoryId % 5] > 35 ? 'red-text' : [12, 18, 25, 8, 32][factory.factoryId % 5] > 15 ? 'yellow-text' : 'green-text'}`}>
                            {[12, 18, 25, 8, 32][factory.factoryId % 5]}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 중앙: 설비별 현황 */}
                  <div className="machines-section">
                    <h4 className="section-title">설비별 현황</h4>
                    <div className="machines-grid">
                      {machines.map((machine) => {
                        const statusInfo = getStatusInfo(machine.status);
                        return (
                          <div
                            key={machine.equipmentId}
                            className="machine-card"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/machine', { state: { machineData: { id: machine.equipmentId, name: machine.equipmentName } } });
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="machine-header">
                              <div className="machine-name">{machine.equipmentName}</div>
                              {machine.alarms > 0 && (
                                <div className="machine-alarm">
                                  <span className="material-symbols-outlined alarm-icon">warning</span>
                                  <div className="alarm-badge">{machine.alarms}</div>
                                </div>
                              )}
                            </div>

                            <div className="machine-status">
                              <div className={`status-dot ${statusInfo.bgColor} ${machine.status === 'OPERATING' ? 'pulse' : ''}`}></div>
                              <span className={`status-text ${statusInfo.color}`}>{statusInfo.text}</span>
                            </div>

                            <div className="machine-info-grid">
                              <div className="info-item">
                                <div className="info-label">샷 카운트</div>
                                <div className="info-value">{formatNumber(machine.shotCount)}</div>
                              </div>
                              <div className="info-item">
                                <div className="info-label">제품명</div>
                                <div className="info-value truncate">{machine.productName}</div>
                              </div>
                            </div>

                            <div className="machine-info-grid">
                              <div className="info-item">
                                <div className="info-label">캐비티</div>
                                <div className="info-value">{machine.cavityCount > 0 ? `${machine.cavityCount}개` : '-'}</div>
                              </div>
                              <div className="info-item">
                                <div className="info-label">사이클타임</div>
                                <div className="info-value">{machine.cycleTime > 0 ? `${machine.cycleTime}초` : '-'}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 우측: 알람 현황 */}
                  <div className="alarms-section">
                    <div className="alarms-header">
                      <h4 className="section-title">실시간 알람</h4>
                      <div className="alarm-summary">
                        <div className="alarm-count critical-count">
                          <div className="count-dot critical-dot"></div>
                          <span>{alarmSummary.critical}</span>
                        </div>
                        <div className="alarm-count warning-count">
                          <div className="count-dot warning-dot"></div>
                          <span>{alarmSummary.warning}</span>
                        </div>
                        <div className="alarm-count info-count">
                          <div className="count-dot info-dot"></div>
                          <span>{alarmSummary.info}</span>
                        </div>
                      </div>
                    </div>

                    <div className="alarms-list">
                      {recentAlarms.length === 0 ? (
                        <div className="no-alarms-message">
                          <span className="material-symbols-outlined">check_circle</span>
                          <span>진행중인 알람이 없습니다.</span>
                        </div>
                      ) : (
                        recentAlarms.map((alarm, index) => {
                          const severityClass = getSeverityColor(alarm.severity);
                          const severityIcon = alarm.severity?.toUpperCase() === 'CRITICAL' ? 'error' : alarm.severity?.toUpperCase() === 'WARNING' ? 'warning' : 'info';
                          return (
                            <div
                              key={`${alarm.alarmId}-${index}`}
                              className={`alarm-item ${severityClass}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/machine', {
                                  state: {
                                    machineData: { id: alarm.equipmentId, name: alarm.equipmentName },
                                    openTab: 'alarm',
                                    alarmId: alarm.alarmId
                                  }
                                });
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="alarm-item-header">
                                <span className="material-symbols-outlined alarm-severity-icon">
                                  {severityIcon}
                                </span>
                                <span className="alarm-id">#{alarm.alarmId}</span>
                                <span className="alarm-machine">{alarm.equipmentName}</span>
                              </div>
                              <div className="alarm-message">{alarm.message}</div>
                              <div className="alarm-time">발생: {formatRelativeTime(alarm.occurredAt)}</div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="alarms-footer">
                      <div className="alarm-stat">
                        <div className="stat-value">{alarmSummary.total}</div>
                        <div className="stat-label">총계</div>
                      </div>
                      <div className="alarm-stat">
                        <div className="stat-value error-text">{alarmSummary.critical}</div>
                        <div className="stat-label">위험</div>
                      </div>
                      <div className="alarm-stat">
                        <div className="stat-value warning-text">{alarmSummary.warning}</div>
                        <div className="stat-label">경고</div>
                      </div>
                      <div className="alarm-stat">
                        <div className="stat-value info-text">{alarmSummary.info}</div>
                        <div className="stat-label">정보</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}

export default EnterpriseFactoryOverview;
